// @flow
import * as React from 'react';

type Props = {
  html: string,
  onChange?: Function,
  onBlur?: Function,
  onKeyUp?: Function,
  onMouseUp?: Function,
  onNodeRemove?: Function,
  className?: string,
  placeHolder?: string,
  tagName?: string,
  disabled?: boolean,
  children?: React.Node,
};

export const WHITE_SPACE_REGEX = /&nbsp;|\u202F|\u00A0/g;
const MENTION_HTML_REGEX = /<span mention="TO(.+?)" id="(\d+?)" data-type="(\w+?)".+?>TO (.+?)<\/span>/;
const ATTACHMENT_HTML_REGEX = /<span(?:".*?"|'.*?'|[^>]*?) +class="attachment"+>(.+?)<\/span>/g;
export const DEFAULT_CONTENT = '<p><br></p>';

export default class Contenteditable extends React.Component<Props> {
  lastHtml: string;
  htmlEl: any = null;
  inCompositionMode: boolean = false;
  currentMentionNode: any = null;
  observer: MutationObserver;

  constructor(props: Props) {
    super(props);
    this.emitChange = this.emitChange.bind(this);
    this.lastHtml = props.html;
  }

  render() {
    const {
      placeHolder,
      html,
      tagName,
      disabled,
      onBlur,
      onKeyUp,
      onMouseUp,
      // eslint-disable-next-line
      onNodeRemove,
      ...props
    } = this.props;

    return React.createElement(tagName || 'div', {
      ...props,
      ref: e => (this.htmlEl = e),
      onInput: this.emitChange,
      onKeyUp: onKeyUp,
      onMouseUp: onMouseUp,
      onBlur: onBlur || this.emitChange,
      onPaste: e => this.handleCopyPaste(e),
      onCompositionEnd: () => this.handleTextComposition(false),
      onCompositionStart: () => this.handleTextComposition(true),
      contentEditable: !disabled,
      dangerouslySetInnerHTML: { __html: html },
      placeholder: placeHolder,
    });
  }

  shouldComponentUpdate(nextProps: Props) {
    const { props, htmlEl, normalizeHtml } = this;
    if (!htmlEl) return true;
    if (
      normalizeHtml(nextProps.html) !== normalizeHtml(htmlEl.innerHTML) &&
      nextProps.html !== props.html
    ) {
      return true;
    }
    return (
      props.disabled !== nextProps.disabled ||
      props.tagName !== nextProps.tagName ||
      props.className !== nextProps.className
    );
  }

  componentDidMount() {
    this.observeMutation();
  }

  componentWillUnMount() {
    this.observer.disconnect();
  }

  componentDidUpdate() {
    if (this.htmlEl && this.props.html !== this.htmlEl.innerHTML) {
      this.htmlEl.innerHTML = this.lastHtml = this.props.html;
    }
  }

  emitChange = (event?: any) => {
    if (!this.htmlEl) return;
    let html = this.content();
    if (this.props.onChange && html !== this.lastHtml) {
      event = event || {};
      event.target = { value: html };
      this.props.onChange(event);
    }
    this.lastHtml = html;
  };

  isEmpty(): boolean {
    return !this.htmlEl.textContent || this.htmlEl.textContent.length == 0;
  }

  keepDomInRightStructure() {
    // keep default node when input is empty
    if (this.htmlEl.innerHTML.length == 0) {
      this.htmlEl.innerHTML = DEFAULT_CONTENT;
      this.selectElement(this.htmlEl.firstChild);
    }
  }

  preventEnterNewLineWhenTextEmpty() {
    if (this.htmlEl.textContent.length == 0 && this.childCount() > 1) {
      this.clear();
    }
  }

  isContainNoText(): boolean {
    let level1Childs = this.htmlEl.childNodes;
    for (let i = 0, l = level1Childs.length; i < l; i++) {
      let level2Childs = level1Childs[i].childNodes;
      for (let j = 0, k = level2Childs.length; j < k; j++) {
        if (this.isTextNode(level2Childs[j])) return false;
      }
    }
    return true;
  }

  normalizeHtml(str: string): string {
    return str && str.replace(/&nbsp;|\u202F|\u00A0/g, ' ');
  }

  focus() {
    let lastChild = this.htmlEl.lastChild;
    while (lastChild.lastChild) {
      lastChild = lastChild.lastChild;
    }
    this.selectElement(lastChild);
    this.moveCaret(lastChild.textContent.length);
  }

  insertToNextSibling(value: string, curNode: any) {
    let nextNode = curNode.nextSibling;
    if (!nextNode || (nextNode.attributes && nextNode.attributes.mention)) {
      nextNode = this.textNode(value);
      curNode.parentNode.insertBefore(nextNode, curNode.nextSibling);
    } else {
      nextNode.textContent = value + nextNode.textContent;
    }
    this.selectElement(nextNode);
    this.moveCaret(nextNode.textContent.length);
  }

  insertToPreviousSibling(value: string, curNode: any) {
    let prevNode = curNode.previousSibling;
    if (!prevNode || (prevNode.attributes && prevNode.attributes.mention)) {
      prevNode = this.textNode(value);
      curNode.parentNode.insertBefore(prevNode, curNode);
    } else {
      prevNode.textContent = value + prevNode.textContent;
    }
    this.selectElement(prevNode);
    this.moveCaret(prevNode.textContent.length);
  }

  replaceAtCurrentNode(newNode: any, curNode: any) {
    let currentPos = this.getCaretCharacterOffsetWithin(curNode);
    if (!curNode.parentNode) return;
    curNode.parentNode.insertBefore(newNode, curNode);
    curNode.remove();
    this.selectElement(newNode);
    this.moveCaret(currentPos);
  }

  getCaretCharacterOffsetWithin(element: any = this.htmlEl): number {
    let caretOffset = 0;
    let doc = element.ownerDocument || element.document;
    let win = doc.defaultView || doc.parentWindow;
    let sel;
    if (typeof win.getSelection != 'undefined') {
      sel = win.getSelection();
      if (sel.rangeCount > 0) {
        let range = win.getSelection().getRangeAt(0);
        let preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretOffset = preCaretRange.toString().length;
      }
    } else if ((sel = doc.selection) && sel.type != 'Control') {
      let textRange = sel.createRange();
      let preCaretTextRange = doc.body.createTextRange();
      preCaretTextRange.moveToElementText(element);
      preCaretTextRange.setEndPoint('EndToEnd', textRange);
      caretOffset = preCaretTextRange.text.length;
    }
    return caretOffset;
  }

  getCaretPosition(): number {
    if (window.getSelection && window.getSelection().getRangeAt) {
      let range = window.getSelection().getRangeAt(0);
      let selectedObj = window.getSelection();
      let rangeCount = 0;
      let childNodes = selectedObj.anchorNode.parentNode.childNodes;
      for (let i = 0; i < childNodes.length; i++) {
        if (childNodes[i] == selectedObj.anchorNode) {
          break;
        }
        if (childNodes[i].outerHTML)
          rangeCount += childNodes[i].outerHTML.length;
        else if (childNodes[i].nodeType == 3) {
          rangeCount += childNodes[i].textContent.length;
        }
      }
      return range.startOffset + rangeCount;
    }
    return -1;
  }

  moveCaret(position: number) {
    let sel, range;
    if (window.getSelection) {
      sel = window.getSelection();
      if (sel.rangeCount > 0) {
        let textNode = sel.focusNode;
        let newOffset = position;
        sel.collapse(textNode, Math.min(textNode.length, newOffset));
      }
    } else if ((sel = window.document.selection)) {
      if (sel.type != 'Control') {
        range = sel.createRange();
        range.move('character', position);
        range.select();
      }
    }
  }

  selectElement(element: any) {
    if (!element) return;
    let range = document.createRange();
    range.selectNodeContents(element);
    let sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  getActualCaretPos(caretPos: number): number {
    let pos = 0;
    for (let i = 0, l = this.htmlEl.childNodes.length; i < l; i++) {
      let lineNode = this.htmlEl.childNodes[i];
      if (caretPos <= lineNode.textContent.length) {
        pos += this.getLineCaretPos(lineNode, caretPos);
        break;
      } else if (lineNode.outerHTML) {
        caretPos -= lineNode.textContent.length;
        pos += lineNode.outerHTML.length;
      }
    }
    return pos;
  }

  getLineCaretPos(lineNode: any, caretPos: number): number {
    let nodeLength,
      delta = 0,
      pos = 0;
    for (let i = 0, l = lineNode.childNodes.length; i < l; i++) {
      let node = lineNode.childNodes[i];
      const isTextNode = this.isTextNode(node);
      nodeLength = isTextNode ? node.textContent.length : node.innerHTML.length;
      if (caretPos <= nodeLength) {
        pos += isTextNode ? caretPos : node.outerHTML.length;
        break;
      } else {
        if (!isTextNode) delta += node.outerHTML.length - node.innerHTML.length;
        pos += nodeLength;
        caretPos -= nodeLength;
      }
    }
    return pos + delta + '<p>'.length;
  }

  handleTextComposition(isEnable: boolean) {
    this.inCompositionMode = isEnable;
    this.handleIfMentionIntegrity(this.currentMentionNode);
  }

  handleEnterNewLineOnMention(mutation: any) {
    if (
      mutation[2] &&
      this.isMentionNode(mutation[2].target) &&
      mutation[2].target.textContent.length == 0
    ) {
      mutation[2].target.replaceWith(this.brTag);
      return;
    }
    for (let m of mutation) {
      if (m.type != 'childList') continue;
      if (
        this.isMentionNode(m.target) &&
        m.target.attributes.mention.value != m.target.textContent
      ) {
        this.replaceAtCurrentNode(
          this.textNode(m.target.textContent),
          m.target,
        );
      }
    }
  }

  observeMutation() {
    this.observer = new MutationObserver(m => {
      this.keepDomInRightStructure();
      this.preventEnterNewLineWhenTextEmpty();
      this.detectAddOrRemoveMentionNode(m);
      this.handleMentionContentChange(m);
      this.handleEnterNewLineOnMention(m);
    });
    this.observer.observe(this.htmlEl, {
      characterData: true,
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  detectAddOrRemoveMentionNode(mutation: any) {
    let removedMentions = [],
      addedMentions = [];
    // detect node get removed or added
    mutation.forEach(m => {
      if (m.target.nodeName == 'SPAN') {
        addedMentions.push(m.target);
      } else if (m.addedNodes.length > 0 && m.removedNodes.length == 0) {
        m.addedNodes.forEach(a => {
          if (this.isMentionNode(a)) addedMentions.push(a);
        });
      } else if (m.addedNodes.length == 0 && m.removedNodes.length > 0) {
        m.removedNodes.forEach(removedNode => {
          if (this.isMentionNode(removedNode)) {
            removedMentions.push(removedNode);
          } else if (removedNode) {
            removedNode.childNodes.forEach(node => {
              if (this.isMentionNode(node)) removedMentions.push(node);
            });
          }
        });
      }
    });
    // keep mention attributes when mention move to a new line
    addedMentions.forEach(added => {
      for (let i = 0; i < removedMentions.length; i++) {
        if (added.textContent == removedMentions[i].textContent) {
          if (!this.isMentionNode(added)) {
            this.replaceAtCurrentNode(removedMentions[i], added);
          }
          removedMentions.splice(i, 1);
          break;
        }
      }
    });
    // notify removed mentions
    removedMentions.forEach(r => this.notifyNodeRemove(r));
  }

  notifyNodeRemove(node: any) {
    this.props.onNodeRemove && this.props.onNodeRemove(node);
    // Move caret to prevent default action of contenteditable
    // which add font style tag automatically
    this.moveCaret(this.getCaretCharacterOffsetWithin());
  }

  isMentionNode(node: any): boolean {
    return (
      node &&
      node.attributes &&
      (node.attributes.mention || node.attributes['data-mention-id'])
    );
  }

  isCorruptMention(node: any): boolean {
    return (
      (node && node.nodeName == 'SPAN' && !node.attributes.mention) ||
      (this.isMentionNode(node) &&
        node.attributes.mention.value != node.textContent)
    );
  }

  handleMentionContentChange(mutation: any) {
    if (
      !this.isMentionNode(mutation[0].target.parentNode) ||
      mutation[0].type != 'characterData'
    )
      return;
    let mentionNode = mutation[0].target.parentNode;
    if (this.handleIfMentionIntegrity(mentionNode)) return;
    const value = mentionNode.textContent;
    const mentionValue = mentionNode.attributes.mention.value;
    if (mentionValue.includes(value)) {
      let parentNode = mentionNode.parentNode;
      if (parentNode.childNodes.length > 1) {
        this.moveCaretToSibling(mentionNode);
        mentionNode.remove();
      } else {
        this.replaceAtCurrentNode(this.brTag, mentionNode);
      }
      this.notifyNodeRemove(mentionNode);
    } else {
      this.replaceAtCurrentNode(this.textNode(value), mentionNode);
      this.notifyNodeRemove(mentionNode);
    }
    this.emitChange();
  }

  moveCaretToSibling(node: any) {
    if (node.previousSibling) {
      let nextFocusNode =
        node.previousSibling.lastChild || node.previousSibling;
      this.selectElement(nextFocusNode);
      this.moveCaret(nextFocusNode.textContent.length);
    } else if (node.nextSibling) {
      let nextFocusNode = node.nextSibling.firstChild || node.nextSibling;
      this.selectElement(nextFocusNode);
      this.moveCaret(0);
    }
  }

  handleIfMentionIntegrity(mentionNode: any) {
    if (!mentionNode) return;
    const value = mentionNode.textContent;
    const mentionValue = mentionNode.attributes.mention.value;
    if (value === mentionValue) return true;
    let pos = value.indexOf(mentionValue);
    if (pos == 0 || (pos > 0 && pos + mentionValue.length == value.length)) {
      if (this.inCompositionMode || !mentionNode) {
        this.currentMentionNode = mentionNode;
      } else if (pos == 0) {
        let tail = value.substr(mentionValue.length, value.length);
        mentionNode.textContent = mentionValue;
        this.insertToNextSibling(tail, mentionNode);
      } else {
        let head = value.substr(0, pos);
        mentionNode.textContent = mentionValue;
        this.insertToPreviousSibling(head, mentionNode);
      }
      this.emitChange();
      return true;
    }
    return false;
  }

  toRawMessage(): string {
    let rawMsg = '';
    this.htmlEl.childNodes.forEach(line => {
      rawMsg +=
        this.lineToRawMessage(line.innerHTML || line.textContent) + '\n';
    });
    return rawMsg;
  }

  lineToRawMessage(html: string): string {
    if (!html) return '';
    return html
      .replace(MENTION_HTML_REGEX, (match, mention, id, type, name) => {
        return `[TO id:${id} type:${type}]${name}[/TO]`;
      })
      .replace(ATTACHMENT_HTML_REGEX, () => {
        return '[FILE/]';
      })
      .replace(/(<br>)+/, '');
  }

  childCount(): number {
    return this.htmlEl.childNodes.length;
  }

  content(): string {
    return this.htmlEl.innerHTML;
  }

  clear() {
    while (this.htmlEl.firstChild) {
      this.htmlEl.removeChild(this.htmlEl.firstChild);
    }
  }

  removeMentionNodeById(id: string) {
    let mentionNodes = this.htmlEl.getElementsByClassName('mention');
    for (let node of mentionNodes) {
      if (node.id == id) {
        let parent = node.parentNode;
        parent.childNodes.length == 1 ? parent.remove() : node.remove();
        break;
      }
    }
  }

  handleCopyPaste(e: any) {
    e.preventDefault();
    e.stopPropagation();
    let paste = this.sanitizeClipboard(e.clipboardData || window.clipboardData);
    document.execCommand('insertHTML', false, paste);
    this.handlePasteRightOnMention();
    this.emitChange(e);
  }

  handlePasteRightOnMention() {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;
    const pastedNode = selection.getRangeAt(0).startContainer;
    setTimeout(() => {
      if (this.isCorruptMention(pastedNode.nextSibling))
        this.convertToTextNode(pastedNode.nextSibling);
      if (this.isCorruptMention(pastedNode.previousSibling))
        this.convertToTextNode(pastedNode.previousSibling);
    }, 100);
  }

  sanitizeClipboard(clipboard: any) {
    return clipboard
      .getData('text')
      .split('\n')
      .reduce((a, s) => (a += `<p>${s.length > 0 ? s : '<br>'}</p>`), '');
  }

  nodeTraversal(node: any, callback: (node: any) => void) {
    for (let child of node.childNodes) {
      callback(child);
      this.nodeTraversal(child, callback);
    }
  }

  get brTag() {
    return document.createElement('br');
  }

  convertToTextNode(node: any) {
    node.replaceWith(this.textNode(node.textContent));
  }

  pNode(content: string) {
    const node = document.createElement('p');
    if (content.length == 0) {
      node.appendChild(this.brTag);
    } else {
      node.innerHTML = content;
    }
    return node;
  }

  textNode(value: string) {
    return document.createTextNode(value);
  }

  isTextNode(node: any) {
    return node.nodeName == '#text';
  }

  isBrNode(node: any) {
    return node.nodeName == 'BR';
  }

  textContent(): string {
    return this.htmlEl.textContent;
  }
}
